import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, FormControlLabel, Checkbox,
  Autocomplete, Alert, Typography, Box,
} from '@mui/material';

const API = process.env.REACT_APP_API_BASE_URL;
const BID = process.env.REACT_APP_DECORMANAGER_BUILDER_ID;

const AVAILABLE_PERMISSIONS = [
  { value: 'deficiencies:read', label: 'Read Deficiencies' },
  { value: 'deficiencies:write', label: 'Write Deficiencies' },
];

const CreateKeyDialog = ({
  open, onClose, onCreated, builders,
}) => {
  const [label, setLabel] = useState('');
  const [builderId, setBuilderId] = useState('');
  const [permissions, setPermissions] = useState(['deficiencies:read']);
  const [monthlyLimit, setMonthlyLimit] = useState('');
  const [enableLimit, setEnableLimit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [createdKey, setCreatedKey] = useState(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const handlePermissionToggle = (perm) => {
    setPermissions((prev) => (prev.includes(perm)
      ? prev.filter((p) => p !== perm)
      : [...prev, perm]));
  };

  const handleCreate = async () => {
    if (!label || !builderId) {
      setError('Label and builder are required');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const token = await window.firebaseAuth.currentUser.getIdToken();
      const resp = await fetch(
        `${API}/auth/external-api-keys/create?builder_id=${BID}&authorization=${token}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            builderId,
            label,
            permissions,
            monthlyLimit: enableLimit ? parseInt(monthlyLimit, 10) || null : null,
          }),
        },
      );
      const result = await resp.json();
      if (result.success) {
        setCreatedKey(result.data.plaintextKey);
        onCreated();
      } else {
        setError(result.error?.message || 'Failed to create key');
      }
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(createdKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleClose = () => {
    setLabel('');
    setBuilderId('');
    setPermissions(['deficiencies:read']);
    setMonthlyLimit('');
    setEnableLimit(false);
    setCreatedKey(null);
    setError('');
    setCopied(false);
    onClose();
  };

  if (createdKey) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>API Key Created</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Copy this key now — it will not be shown again.
          </Alert>
          <Box sx={
{
  p: 2, bgcolor: '#f5f5f5', borderRadius: 1, fontFamily: 'monospace', fontSize: 13, wordBreak: 'break-all',
}
}
          >
            {createdKey}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCopy} variant="contained">
            {copied ? 'Copied!' : 'Copy to Clipboard'}
          </Button>
          <Button onClick={handleClose}>Done</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create API Key</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <TextField
          label="Label"
          placeholder="e.g. Acme Integration"
          fullWidth
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          sx={{ mt: 1, mb: 2 }}
        />

        <Autocomplete
          options={[...builders].sort((a, b) => a.builderId.localeCompare(b.builderId))}
          getOptionLabel={(option) => `${option.builderId} — ${option.companyName || ''} — ${option.salescenterName || ''}`}
          filterOptions={
            (options, { inputValue }) => {
              const q = inputValue.toLowerCase();
              return options.filter((o) => (o.companyName || '').toLowerCase().includes(q)
                || (o.salescenterName || '').toLowerCase().includes(q)
                || o.builderId.toLowerCase().includes(q));
            }
          }
          value={builders.find((b) => b.builderId === builderId) || null}
          onChange={(_, newValue) => setBuilderId(newValue ? newValue.builderId : '')}
          renderInput={(params) => <TextField {...params} label="Builder" />}
          fullWidth
          sx={{ mb: 2 }}
        />

        <Typography variant="subtitle2" sx={{ mb: 1 }}>Permissions</Typography>
        {
AVAILABLE_PERMISSIONS.map((perm) => (
  <FormControlLabel
    key={perm.value}
    control={
(
  <Checkbox
    checked={permissions.includes(perm.value)}
    onChange={() => handlePermissionToggle(perm.value)}
  />
            )
}
    label={perm.label}
  />
))
}

        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={<Checkbox checked={enableLimit} onChange={(e) => setEnableLimit(e.target.checked)} />}
            label="Enable monthly request limit"
          />
          {
enableLimit && (
  <TextField
    label="Monthly limit"
    type="number"
    value={monthlyLimit}
    onChange={(e) => setMonthlyLimit(e.target.value)}
    sx={{ ml: 2, width: 150 }}
    size="small"
  />
)
}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={handleCreate} variant="contained" disabled={loading}>
          {loading ? 'Creating...' : 'Create Key'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateKeyDialog;
